import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Building2, ArrowRight, Users, Cpu, Truck, LineChart } from "lucide-react";

const BUCKETS = [
  { icon: Truck, label: "Fleet Claims", desc: "Manage 100-10,000 vehicles from one dashboard" },
  { icon: Cpu, label: "B2B Claim API", desc: "Embed our AI in your app in 3 lines of code" },
  { icon: Users, label: "Dedicated CSM", desc: "A human account manager for every enterprise" },
  { icon: LineChart, label: "Analytics", desc: "Real-time claim ratio, cycle time & cost trend" },
];

export default function EnterpriseCTA() {
  return (
    <section className="py-24 relative overflow-hidden" data-testid="enterprise-cta">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative rounded-[32px] overflow-hidden bg-slate-950 text-white p-8 sm:p-12 lg:p-16 grain">
          {/* Aurora inside dark card */}
          <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-blue-800/40 blur-3xl" />
          <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-orange-500/30 blur-3xl" />
          <div className="absolute inset-0 dot-grid opacity-30" />

          <div className="relative grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-xs font-semibold text-white mb-5">
                <Building2 className="w-3.5 h-3.5" /> For Enterprises · Fleets · Corporate
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05] mb-4">
                Power your <span className="gradient-text">fleet claims</span><br />
                with AI infrastructure.
              </h2>
              <p className="text-slate-300 max-w-xl mb-6 leading-relaxed">
                Ola, Rapido-style fleets, corporate car pools, and rentals use miclaim's Claim API
                to file, track, and settle motor claims at scale — with cycle-time SLAs, dedicated
                CSM support, and per-vehicle analytics.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/enterprise"
                  className="btn-signature rounded-xl px-6 py-3 font-semibold inline-flex items-center gap-2"
                  data-testid="enterprise-book-demo"
                >
                  Book a demo <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/enterprise#api"
                  className="rounded-xl px-6 py-3 font-semibold bg-white/5 border border-white/15 hover:bg-white/10 transition-colors inline-flex items-center gap-2"
                  data-testid="enterprise-api-docs"
                >
                  View API docs
                </Link>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {BUCKETS.map((b, i) => (
                <motion.div
                  key={b.label}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="rounded-2xl bg-white/5 border border-white/10 p-5 hover:bg-white/10 hover:border-white/20 transition-colors backdrop-blur-sm"
                >
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-700 to-orange-500 flex items-center justify-center mb-3 shadow-lg">
                    <b.icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="font-heading font-bold text-white text-sm mb-1">{b.label}</div>
                  <p className="text-xs text-slate-400 leading-relaxed">{b.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
